<script setup>
// Galeria de papéis (Fase 2, tela 00 do desenho, spec §4.1): "Comece a partir de um modelo". Cards do catálogo
// (GET captain/templates): Vendas ativo; os drafts (Atendimento, Comércio) com o badge "Em breve". Cada card mostra
// cenários (pill "Ativo ao instalar"/"Inativo") e ferramentas pela chave — nunca nome de fornecedor (regra do dono).
// "Usar modelo" abre o dialog de slots do template; instalado, vai para o Get started do assistente criado.
// "Assistente em branco" abre o CreateAssistantDialog de sempre.
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import PolarisTemplatesAPI from 'dashboard/api/polaris/templates';

import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';
import CreateAssistantDialog from 'dashboard/components-next/polaris/pageComponents/assistant/CreateAssistantDialog.vue';
import InstallTemplateDialog from './InstallTemplateDialog.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
// O install é admin-only no backend (TemplatesPolicy): o botão "Usar modelo" não aparece para agente.
const { isAdmin } = useAdmin();

const templates = ref([]);
const isFetching = ref(true);
const installing = ref(false);
const installError = ref('');
const selectedTemplate = ref(null);
const createAssistantDialogRef = ref(null);

const fetchTemplates = async () => {
  isFetching.value = true;
  try {
    const { data } = await PolarisTemplatesAPI.get();
    templates.value = data.payload;
  } catch {
    templates.value = [];
    useAlert(t('POLARIS.TEMPLATES.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

onMounted(fetchTemplates);

const install = async (template, slots) => {
  installing.value = true;
  installError.value = '';
  try {
    const { data } = await PolarisTemplatesAPI.install(template.key, slots);
    await store.dispatch('polarisAssistants/get');
    selectedTemplate.value = null;
    router.push({
      name: 'polaris_assistants_get_started_index',
      params: { accountId: route.params.accountId, assistantId: data.id },
    });
  } catch (error) {
    const messages = error?.response?.data?.messages;
    installError.value = messages?.length
      ? messages.join('; ')
      : t('POLARIS.TEMPLATES.INSTALL.ERROR');
    if (!selectedTemplate.value) useAlert(installError.value);
  } finally {
    installing.value = false;
  }
};

const openInstall = template => {
  installError.value = '';
  if (template.slots_form?.length) {
    selectedTemplate.value = template;
  } else {
    install(template, {});
  }
};

const onBlankAssistantCreated = assistant => {
  router.push({
    name: 'polaris_assistants_get_started_index',
    params: { accountId: route.params.accountId, assistantId: assistant.id },
  });
};
</script>

<template>
  <PageLayout
    :header-title="t('POLARIS.TEMPLATES.HEADER')"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template v-if="isAdmin" #headerActions>
      <Button
        variant="outline"
        size="sm"
        @click="createAssistantDialogRef.dialogRef.open()"
      >
        {{ t('POLARIS.TEMPLATES.BLANK_ASSISTANT') }}
      </Button>
    </template>
    <template #body>
      <p class="m-0 -mt-2 text-sm text-polaris-muted-foreground">
        {{ t('POLARIS.TEMPLATES.SUBTITLE') }}
      </p>
      <div v-if="isFetching" class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Skeleton v-for="n in 3" :key="n" class="h-56 rounded-polaris-lg" />
      </div>
      <div v-else class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <section
          v-for="template in templates"
          :key="template.key"
          class="flex flex-col gap-3 rounded-polaris-lg border border-solid border-polaris-border bg-polaris-card p-4"
        >
          <div class="flex items-start justify-between gap-2">
            <h2 class="m-0 text-base font-semibold text-polaris-foreground">
              {{ template.name }}
            </h2>
            <span
              v-if="template.status === 'draft'"
              class="shrink-0 rounded-polaris-md bg-polaris-muted px-2 py-0.5 text-xs font-medium text-polaris-muted-foreground"
            >
              {{ t('POLARIS.TEMPLATES.COMING_SOON') }}
            </span>
          </div>
          <p class="m-0 text-sm text-polaris-muted-foreground">
            {{ template.description }}
          </p>
          <div v-if="template.scenarios.length" class="flex flex-col gap-1.5">
            <span
              class="text-xs font-semibold uppercase tracking-wider text-polaris-muted-foreground"
            >
              {{ t('POLARIS.TEMPLATES.SCENARIOS_TITLE') }}
            </span>
            <div
              v-for="scenario in template.scenarios"
              :key="scenario.title"
              class="flex items-center justify-between gap-2 text-sm text-polaris-foreground"
            >
              <span class="truncate">{{ scenario.title }}</span>
              <span
                class="shrink-0 rounded-polaris-md px-2 py-0.5 text-xs font-medium"
                :class="
                  scenario.enabled_default
                    ? 'bg-polaris-success text-polaris-success-foreground'
                    : 'bg-polaris-muted text-polaris-muted-foreground'
                "
              >
                {{
                  scenario.enabled_default
                    ? t('POLARIS.TEMPLATES.SCENARIO_ON')
                    : t('POLARIS.TEMPLATES.SCENARIO_OFF')
                }}
              </span>
            </div>
          </div>
          <div v-if="template.tools.length" class="flex flex-col gap-1.5">
            <span
              class="text-xs font-semibold uppercase tracking-wider text-polaris-muted-foreground"
            >
              {{ t('POLARIS.TEMPLATES.TOOLS_TITLE') }}
            </span>
            <div class="flex flex-wrap gap-1">
              <span
                v-for="tool in template.tools"
                :key="tool.key"
                class="rounded-polaris-md bg-polaris-accent px-2 py-0.5 text-xs text-polaris-accent-foreground"
                :class="
                  tool.required
                    ? ''
                    : 'border border-dashed border-polaris-border bg-transparent'
                "
              >
                {{ tool.key.replaceAll('_', ' ') }}
              </span>
            </div>
          </div>
          <div
            v-if="template.status === 'active' && isAdmin"
            class="mt-auto pt-2"
          >
            <Button
              class="w-full"
              :disabled="installing"
              @click="openInstall(template)"
            >
              {{ t('POLARIS.TEMPLATES.USE_TEMPLATE') }}
            </Button>
          </div>
        </section>
      </div>
    </template>
    <InstallTemplateDialog
      :template="selectedTemplate"
      :installing="installing"
      :error-message="installError"
      @submit="slots => install(selectedTemplate, slots)"
      @close="selectedTemplate = null"
    />
    <CreateAssistantDialog
      ref="createAssistantDialogRef"
      type="create"
      @created="onBlankAssistantCreated"
    />
  </PageLayout>
</template>
